import React, { useCallback, useEffect } from 'react';
import classNames from 'classnames';
import useBrowserState from '@modules/BrowserModule/useBrowserState';
import { useAudioDevices } from '../../useAudioDevices';
import { audioDevicesState } from '../../audioDevicesState';
import {
  speakerTestService,
  ringTestService,
} from '../../utils/audioPlaybackTestService';
import SoftPhoneModule, { AudioDeviceCombobox } from '@modules/SoftPhoneModule';
import {
  audioDeviceStrings,
  getInitializationFieldErrorMessage,
} from '../audioDeviceStrings';
import { AllowMicrophoneButton } from './AllowMicrophoneButton';
import { MicrophoneTestButton } from './MicrophoneTestButton';
import { AudioTestButton } from './AudioTestButton';
import {
  deriveEffectiveErrorCategory,
  deriveSwitchErrors,
  getMicPlaceholder,
  getSpeakerPlaceholder,
} from './audioDevicePanelHelpers';
import styles from './AudioDevicePanel.module.css';
import { RingOutputSection } from './RingOutputSection';

export interface AudioDevicePanelProps {
  className?: string;
  onDeviceSelected?: () => void;
  onClose?: () => void;
}

/**
 * Input and output device pickers backed by {@link audioDevicesState}.
 * Includes an optional ring output section for routing ringtones to a separate speaker.
 */
export const AudioDevicePanel: React.FC<AudioDevicePanelProps> = ({
  className,
  onDeviceSelected,
  onClose,
}) => {
  const state = useAudioDevices();
  const { microphoneAccess } = useBrowserState();

  const {
    audioInputDevices,
    audioOutputDevices,
    currentInputDeviceId,
    currentOutputDeviceId,
    currentRingDeviceId,
    isDevicesReady,
    errorCategory,
    lastSetDevicesError,
    isTestingMicrophone,
    isTestingSpeaker,
    speakerTestError,
    isRingOutputEnabled,
    isTestingRingSpeaker,
    ringSpeakerTestError,
    microphoneAnalyser,
  } = state;

  useEffect(() => {
    return () => {
      SoftPhoneModule.stopMicMonitor();
      speakerTestService.stopTest();
      ringTestService.stopTest();
      audioDevicesState.isTestingSpeaker = false;
      audioDevicesState.isTestingRingSpeaker = false;
      audioDevicesState.ringSpeakerTestError = null;
    };
  }, []);

  const effectiveErrorCategory = deriveEffectiveErrorCategory(
    errorCategory,
    isDevicesReady,
    microphoneAccess,
  );

  const micPermissionDenied =
    !isDevicesReady && effectiveErrorCategory === 'permission_denied';

  const micInitFailed = !isDevicesReady && !!effectiveErrorCategory;

  const microphoneInitializationError =
    micInitFailed && effectiveErrorCategory
      ? getInitializationFieldErrorMessage(effectiveErrorCategory, 'microphone')
      : null;

  const speakerInitFailed = !isDevicesReady && !!errorCategory;
  const speakerInitializationError =
    speakerInitFailed && errorCategory !== 'permission_denied' && errorCategory
      ? getInitializationFieldErrorMessage(errorCategory, 'speaker')
      : null;

  const noDevicesPlaceholder = getMicPlaceholder(
    micInitFailed,
    micPermissionDenied,
    audioInputDevices.length,
  );
  const noOutputDevicesPlaceholder = getSpeakerPlaceholder(
    micPermissionDenied,
    speakerInitFailed,
    audioOutputDevices.length,
  );

  const { microphoneSwitchError, speakerSwitchError, ringOutputSwitchError } =
    deriveSwitchErrors(isDevicesReady, lastSetDevicesError);

  const comboboxDisabled = !isDevicesReady;
  const micComboboxLoading = !isDevicesReady && !effectiveErrorCategory;
  const speakerComboboxLoading = !isDevicesReady && !effectiveErrorCategory;

  const setDevicesIfChanged = useCallback(
    (nextInput: string, nextOutput: string) => {
      const sameInput = nextInput === currentInputDeviceId;
      const sameOutput = nextOutput === currentOutputDeviceId;
      if (sameInput && sameOutput) {
        return;
      }
      const ringId = isRingOutputEnabled
        ? currentRingDeviceId ?? nextOutput
        : nextOutput;
      SoftPhoneModule.setAudioDevices(nextInput, nextOutput, ringId);
    },
    [
      currentInputDeviceId,
      currentOutputDeviceId,
      currentRingDeviceId,
      isRingOutputEnabled,
    ],
  );

  const handleMicrophoneChange = useCallback(
    (deviceId: string) => {
      if (!isDevicesReady || currentOutputDeviceId === undefined) {
        return;
      }
      setDevicesIfChanged(deviceId, currentOutputDeviceId);
      onDeviceSelected?.();
    },
    [
      isDevicesReady,
      currentOutputDeviceId,
      setDevicesIfChanged,
      onDeviceSelected,
    ],
  );

  const handleSpeakerChange = useCallback(
    (deviceId: string) => {
      if (!isDevicesReady || currentInputDeviceId === undefined) {
        return;
      }
      if (isTestingSpeaker) {
        speakerTestService.stopTest();
        audioDevicesState.isTestingSpeaker = false;
      }
      setDevicesIfChanged(currentInputDeviceId, deviceId);
      onDeviceSelected?.();
    },
    [
      isDevicesReady,
      currentInputDeviceId,
      isTestingSpeaker,
      setDevicesIfChanged,
      onDeviceSelected,
    ],
  );

  return (
    <div
      className={classNames(styles.panel, className)}
      data-testid='audio-device-panel'
    >
      <AudioDeviceCombobox
        data-testid='audio-device-panel-microphone'
        devices={audioInputDevices}
        currentDeviceId={currentInputDeviceId}
        onChange={handleMicrophoneChange}
        label={audioDeviceStrings.labels.microphone}
        disabled={comboboxDisabled}
        loading={micComboboxLoading}
        placeholderText={noDevicesPlaceholder}
        initializationDescription={microphoneInitializationError}
        errorMessage={microphoneSwitchError}
        testButton={
          micPermissionDenied ? (
            <AllowMicrophoneButton onClose={onClose} />
          ) : (
            <MicrophoneTestButton
              isTestingMicrophone={isTestingMicrophone}
              microphoneAnalyser={microphoneAnalyser}
              disabled={comboboxDisabled}
            />
          )
        }
      />

      <AudioDeviceCombobox
        data-testid='audio-device-panel-speaker'
        devices={audioOutputDevices}
        currentDeviceId={currentOutputDeviceId}
        onChange={handleSpeakerChange}
        label={audioDeviceStrings.labels.speaker}
        disabled={comboboxDisabled}
        loading={speakerComboboxLoading}
        placeholderText={noOutputDevicesPlaceholder}
        initializationDescription={speakerInitializationError}
        errorMessage={speakerSwitchError ?? speakerTestError}
        testButton={
          <AudioTestButton
            deviceId={currentOutputDeviceId}
            isTesting={isTestingSpeaker}
            disabled={comboboxDisabled || audioOutputDevices.length === 0}
            testService={speakerTestService}
            setTesting={(v) => {
              audioDevicesState.isTestingSpeaker = v;
            }}
            setError={(v) => {
              audioDevicesState.speakerTestError = v;
            }}
            data-testid='audio-device-panel-test-speaker'
          />
        }
      />

      <RingOutputSection
        className={styles.fullWidth}
        isRingOutputEnabled={isRingOutputEnabled}
        currentRingDeviceId={currentRingDeviceId}
        currentOutputDeviceId={currentOutputDeviceId}
        currentInputDeviceId={currentInputDeviceId}
        audioOutputDevices={audioOutputDevices}
        disabled={comboboxDisabled}
        loading={speakerComboboxLoading}
        ringOutputSwitchError={ringOutputSwitchError}
        isTestingRingSpeaker={isTestingRingSpeaker}
        ringSpeakerTestError={ringSpeakerTestError}
      />
    </div>
  );
};
